import { type PostType } from '@tribeplatform/gql-client/types'

import type { I18nShape } from '../../i18n/types.js'
import {
  ArrayOptions,
  ColorModes,
  MemberGroups,
  ReactionStates,
  ReactionTypes,
  ReadOptions,
  WriteOptions,
} from '../types.js'

export const reactionTypeOptionsFactory = (intl: I18nShape) => {
  const { $t } = intl
  return [
    {
      value: ReactionTypes.all,
      text: $t({ defaultMessage: 'All Emojis', id: 'Admin.CMS.AllEmojis' }),
    },
    {
      value: ReactionTypes.custom,
      text: $t({
        defaultMessage: 'Custom reactions',
        id: 'Admin.CMS.CustomReactions',
      }),
    },
    {
      value: ReactionTypes.none,
      text: $t({
        defaultMessage: 'Disable reactions',
        id: 'Admin.CMS.DisableReactions',
      }),
    },
  ]
}

export const replyTypeOptionsFactory = (
  intl: I18nShape,
  replyTypes: PostType[],
) => {
  const { $t } = intl
  return [
    {
      text: $t({
        defaultMessage: 'Disable replies',
        id: 'Admin.CMS.ReplyType.Disable',
      }),
      value: null,
    },
    ...replyTypes.map(replyType => ({
      text: replyType.name,
      value: replyType.id,
    })),
  ]
}

export const arrayOptionsFactory: (intl: I18nShape) => {
  value: ArrayOptions
  text: string
}[] = ({ $t }) => [
  {
    value: ArrayOptions.single,
    text: $t({
      defaultMessage: 'Single',
      id: 'Generics.Single',
    }),
  },
  {
    value: ArrayOptions.multiple,
    text: $t({
      defaultMessage: 'Multiple',
      id: 'Generics.Multiple',
    }),
  },
]

export const readOptionsFactory: (intl: I18nShape) => {
  value: ReadOptions
  text: string
}[] = ({ $t }) => [
  {
    value: ReadOptions.everyone,
    text: $t({
      defaultMessage: 'Everyone',
      id: 'Admin.PermissionGroup.All',
    }),
  },
  {
    value: ReadOptions.admin,
    text: $t({
      defaultMessage: 'Staff only',
      id: 'Admin.PermissionGroup.StaffOnly',
    }),
  },
  {
    value: ReadOptions.own,
    text: $t({
      defaultMessage: 'Author and staff',
      id: 'Admin.PermissionGroup.AuthorAndStaff',
    }),
  },
]

export const writeOptionsFactory: (intl: I18nShape) => {
  value: WriteOptions
  text: string
}[] = ({ $t }) => [
  {
    value: WriteOptions.admin,
    text: $t({
      defaultMessage: 'Staff only',
      id: 'Admin.PermissionGroup.StaffOnly',
    }),
  },
  {
    value: WriteOptions.own,
    text: $t({
      defaultMessage: 'Author and staff',
      id: 'Admin.PermissionGroup.AuthorAndStaff',
    }),
  },
]

export const memberGroupsFactory: (intl: I18nShape) => {
  value: MemberGroups
  text: string
}[] = ({ $t }) => [
  {
    value: MemberGroups.all,
    text: $t({
      defaultMessage: 'All members',
      id: 'Generics.AllMembers',
    }),
  },
  {
    value: MemberGroups.staff,
    text: $t({
      defaultMessage: 'Staff members',
      id: 'Generics.StaffMembers',
    }),
  },
  {
    value: MemberGroups.member,
    text: $t({
      defaultMessage: 'Regular members',
      id: 'Generics.RegularMembers',
    }),
  },
]

export const reactionStatesFactory: (intl: I18nShape) => {
  value: ReactionStates
  text: string
}[] = ({ $t }) => [
  {
    value: ReactionStates.default,
    text: $t({
      defaultMessage: 'Default',
      id: 'Generics.Default',
    }),
  },
  {
    value: ReactionStates.active,
    text: $t({
      defaultMessage: 'Active',
      id: 'Generics.Active',
    }),
  },
]

export const colorModesFactory: (intl: I18nShape) => {
  value: ColorModes
  text: string
}[] = ({ $t }) => [
  {
    value: ColorModes.default,
    text: $t({
      defaultMessage: 'Use theme color',
      id: 'Generics.ThemeColor',
    }),
  },
  {
    value: ColorModes.custom,
    text: $t({
      defaultMessage: 'Custom color',
      id: 'Generics.CustomColor',
    }),
  },
]
